import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  MenuItem,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { lmsAPI, teachersAPI } from '../../services/api';
import PageBackButton from '../../components/PageBackButton';
import { formatDateInViewerTZ, formatDateTimeInViewerTZ, getViewerTimezone } from '../../utils/dateTimeUtils';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const AssignmentManager = () => {
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const queryClient = useQueryClient();
  const { data: teachersData, isLoading: loadingTeachers } = useQuery(['teachers', 'lms-assignments'], () =>
    teachersAPI.getAll({ limit: 500, status: 'active' }).then((res) => res.data)
  );
  const { data: modulesData } = useQuery(['lms-modules'], () => lmsAPI.getModules().then((res) => res.data));
  const { data: monthsData } = useQuery(['lms-months'], () => lmsAPI.getMonths().then((res) => res.data));
  const { data: resourcesData } = useQuery(['lms-resources'], () => lmsAPI.getResources().then((res) => res.data));
  const { data: assignmentsData, isLoading: loadingAssignments } = useQuery(['lms-assignments'], () =>
    lmsAPI.getAssignments().then((res) => res.data)
  );

  const [form, setForm] = useState({
    teacher_id: '',
    assignment_type: 'module',
    module_id: '',
    month_id: '',
    module_ids: [],
    month_ids: [],
    resource_ids: [],
  });

  const resetSelections = (assignmentType = form.assignment_type) => ({
    teacher_id: form.teacher_id,
    assignment_type: assignmentType,
    module_id: '',
    month_id: '',
    module_ids: [],
    month_ids: [],
    resource_ids: [],
  });

  const assignmentMutation = useMutation((payload) => lmsAPI.createAssignment(payload), {
    onSuccess: (res) => {
      const { message, data } = res.data || {};
      if (data?.created?.length) {
        toast.success(message || 'Assignments created');
      }
      if (data?.skipped?.length) {
        data.skipped.forEach((item) => toast.error(item.message, { duration: 6000 }));
      }
      if (!data?.created?.length && data?.skipped?.length) {
        toast.error(message || 'No new assignments created');
      }
      queryClient.invalidateQueries(['lms-assignments']);
      setForm({
        teacher_id: '',
        assignment_type: 'module',
        module_id: '',
        month_id: '',
        module_ids: [],
        month_ids: [],
        resource_ids: [],
      });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create assignment'),
  });

  const deleteMutation = useMutation((id) => lmsAPI.deleteAssignment(id), {
    onSuccess: () => {
      toast.success('Assignment removed');
      queryClient.invalidateQueries(['lms-assignments']);
    },
    onError: () => toast.error('Failed to remove assignment'),
  });

  const modules = useMemo(
    () => modulesData?.data || [],
    [modulesData?.data]
  );
  const assignments = useMemo(
    () => assignmentsData?.data || [],
    [assignmentsData?.data]
  );

  const selectedTeacherAssignments = useMemo(() => {
    if (!form.teacher_id) return [];
    return assignments.filter((a) => a.teacher_id === form.teacher_id);
  }, [assignments, form.teacher_id]);

  const filteredMonths = useMemo(() => {
    const months = monthsData?.data || [];
    if ((form.assignment_type === 'month' || form.assignment_type === 'resource') && form.module_id) {
      return months.filter((m) => m.module_id === form.module_id);
    }
    return months;
  }, [monthsData?.data, form.assignment_type, form.module_id]);

  const filteredResources = useMemo(() => {
    const resources = resourcesData?.data || [];
    if (form.assignment_type === 'resource' && form.month_id) {
      return resources.filter((r) => (r.activity_id || r.month_id) === form.month_id);
    }
    return resources;
  }, [resourcesData?.data, form.assignment_type, form.month_id]);

  const availableModules = useMemo(() => {
    if (!form.teacher_id || form.assignment_type !== 'module') return modules;
    const assignedModuleIds = new Set(
      selectedTeacherAssignments
        .filter((a) => a.assignment_type === 'module')
        .map((a) => a.module_id)
    );
    return modules.filter((m) => !assignedModuleIds.has(m.id));
  }, [modules, form.teacher_id, form.assignment_type, selectedTeacherAssignments]);

  const availableMonths = useMemo(() => {
    if (!form.teacher_id || form.assignment_type !== 'month') return filteredMonths;
    const assignedMonthIds = new Set(
      selectedTeacherAssignments
        .filter((a) => a.assignment_type === 'month')
        .map((a) => a.month_id)
    );
    return filteredMonths.filter((m) => !assignedMonthIds.has(m.id));
  }, [filteredMonths, form.teacher_id, form.assignment_type, selectedTeacherAssignments]);

  const availableResources = useMemo(() => {
    if (!form.teacher_id || form.assignment_type !== 'resource') return filteredResources;
    const assignedResourceIds = new Set(
      selectedTeacherAssignments
        .filter((a) => a.assignment_type === 'video' || a.assignment_type === 'resource')
        .map((a) => a.resource_id || a.video_id)
    );
    return filteredResources.filter((r) => !assignedResourceIds.has(r.id));
  }, [filteredResources, form.teacher_id, form.assignment_type, selectedTeacherAssignments]);

  const selectedModules = useMemo(
    () => availableModules.filter((m) => form.module_ids.includes(m.id)),
    [availableModules, form.module_ids]
  );

  const selectedMonths = useMemo(
    () => availableMonths.filter((m) => form.month_ids.includes(m.id)),
    [availableMonths, form.month_ids]
  );

  const selectedResources = useMemo(
    () => availableResources.filter((r) => form.resource_ids.includes(r.id)),
    [availableResources, form.resource_ids]
  );

  const buildAssignmentPayload = () => {
    const payload = {
      teacher_id: form.teacher_id,
      assignment_type: form.assignment_type,
    };
    if (form.assignment_type === 'module') payload.module_ids = form.module_ids;
    if (form.assignment_type === 'month') payload.month_ids = form.month_ids;
    if (form.assignment_type === 'resource') {
      payload.assignment_type = 'resource';
      payload.resource_ids = form.resource_ids;
    }
    return payload;
  };

  const getSelectedCount = () => {
    if (form.assignment_type === 'module') return form.module_ids.length;
    if (form.assignment_type === 'month') return form.month_ids.length;
    return form.resource_ids.length;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedCount = getSelectedCount();
    if (!form.teacher_id) {
      toast.error('Please select a teacher');
      return;
    }
    if (selectedCount === 0) {
      const label =
        form.assignment_type === 'module'
          ? 'module'
          : form.assignment_type === 'month'
            ? 'activity'
            : 'resource';
      toast.error(`Please select at least one ${label}`);
      return;
    }
    if (form.assignment_type === 'month' && !form.module_id) {
      toast.error('Please select a module to filter activities');
      return;
    }
    if (form.assignment_type === 'resource' && (!form.module_id || !form.month_id)) {
      toast.error('Please select a module and activity to filter resources');
      return;
    }
    assignmentMutation.mutate(buildAssignmentPayload());
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <PageBackButton />
        <Typography variant="h4">LMS Assignments</Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={form.teacher_id ? 7 : 12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Create Assignment
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Teacher"
                    value={form.teacher_id}
                    onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
                    required
                  >
                    {loadingTeachers && (
                      <MenuItem disabled>
                        <CircularProgress size={16} />
                      </MenuItem>
                    )}
                    {teachersData?.data?.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Assignment Type"
                    value={form.assignment_type}
                    onChange={(e) => setForm(resetSelections(e.target.value))}
                    required
                  >
                    <MenuItem value="module">Module</MenuItem>
                    <MenuItem value="month">Activity</MenuItem>
                    <MenuItem value="resource">Learning Resource</MenuItem>
                  </TextField>
                </Grid>

                {form.assignment_type === 'module' && (
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={availableModules}
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      value={selectedModules}
                      onChange={(_, value) =>
                        setForm({ ...form, module_ids: value.map((item) => item.id) })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Modules"
                          placeholder={selectedModules.length ? '' : 'Select one or more modules'}
                          required={selectedModules.length === 0}
                        />
                      )}
                    />
                  </Grid>
                )}

                {form.assignment_type === 'month' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Module"
                        value={form.module_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            module_id: e.target.value,
                            month_ids: [],
                          })
                        }
                        required
                      >
                        {modules.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        multiple
                        size="small"
                        disabled={!form.module_id}
                        options={availableMonths}
                        getOptionLabel={(option) => option.name || ''}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={selectedMonths}
                        onChange={(_, value) =>
                          setForm({ ...form, month_ids: value.map((item) => item.id) })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Activities"
                            placeholder={
                              !form.module_id
                                ? 'Select a module first'
                                : selectedMonths.length
                                  ? ''
                                  : 'Select one or more activities'
                            }
                            required={selectedMonths.length === 0}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                {form.assignment_type === 'resource' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Module"
                        value={form.module_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            module_id: e.target.value,
                            month_id: '',
                            resource_ids: [],
                          })
                        }
                        required
                      >
                        {modules.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Activity"
                        value={form.month_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            month_id: e.target.value,
                            resource_ids: [],
                          })
                        }
                        required
                        disabled={!form.module_id}
                      >
                        {filteredMonths.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        multiple
                        size="small"
                        disabled={!form.month_id}
                        options={availableResources}
                        getOptionLabel={(option) =>
                          `${option.title} (${option.resource_type || 'video'})`
                        }
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={selectedResources}
                        onChange={(_, value) =>
                          setForm({ ...form, resource_ids: value.map((item) => item.id) })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Learning Resources"
                            placeholder={
                              !form.month_id
                                ? 'Select an activity first'
                                : selectedResources.length
                                  ? ''
                                  : 'Select one or more resources'
                            }
                            required={selectedResources.length === 0}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={assignmentMutation.isLoading || getSelectedCount() === 0}
                  >
                    {getSelectedCount() > 1
                      ? `Assign ${getSelectedCount()} items`
                      : 'Assign'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {form.teacher_id && (
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Teacher's Current Assignments
              </Typography>
              {selectedTeacherAssignments.length === 0 ? (
                <Alert severity="info">No assignments yet for this teacher</Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {selectedTeacherAssignments.map((a) => (
                    <Card key={a.id} sx={{ mb: 1 }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Chip label={a.assignment_type} size="small" color="primary" />
                          <Typography variant="caption" color="text.secondary">
                            {formatDateInViewerTZ(a.assigned_at, viewerTimezone)}
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {a.breadcrumb_path || (
                            a.assignment_type === 'module'
                              ? a.module_name
                              : a.assignment_type === 'month'
                                ? a.month_name
                                : a.video_title
                          )}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Assignments
        </Typography>
        {loadingAssignments ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Assigned At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.teacher_name}</TableCell>
                    <TableCell>
                      <Chip label={a.assignment_type} size="small" />
                    </TableCell>
                    <TableCell>
                      {a.breadcrumb_path || (
                        a.assignment_type === 'module'
                          ? a.module_name
                          : a.assignment_type === 'month'
                            ? a.month_name
                            : a.video_title
                      )}
                    </TableCell>
                    <TableCell>{formatDateTimeInViewerTZ(a.assigned_at, viewerTimezone)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deleteMutation.mutate(a.id)}
                        disabled={deleteMutation.isLoading}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {assignments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>No assignments found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AssignmentManager;
